import React from 'react';
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

type FillableColor = 'primary' | 'secondary' | 'error';

interface BaseIconButtonDeviceProps {
    title: string;
    icon: React.ReactNode;
    color?: FillableColor; // <- narrowed
    disabled?: boolean;
    loading?: boolean;
    onClick?: () => void;
    sx?: SxProps<Theme>;
}

// Helpers avoid palette indexing with a union that includes invalid keys
const bgFor = (theme: Theme, color: FillableColor) => {
    switch (color) {
        case 'secondary':
            return theme.palette.secondary.main;
        case 'error':
            return theme.palette.error.main;
        default:
            return theme.palette.primary.main;
    }
};

const hoverBgFor = (theme: Theme, color: FillableColor) => {
    switch (color) {
        case 'secondary':
            return theme.palette.secondary.dark;
        case 'error':
            return theme.palette.error.dark;
        default:
            return theme.palette.primary.dark;
    }
};

const BaseIconButtonDevice: React.FC<BaseIconButtonDeviceProps> = ({
    title,
    icon,
    color = 'primary',
    disabled = false,
    loading = false,
    onClick,
    sx,
}) => {
    const isDisabled = disabled || loading;

    return (
        <Tooltip title={title}>
            {/* span keeps tooltip working when the button is disabled */}
            <span>
                <IconButton
                    // keep MUI states & focus handling, but we drive the fill via sx
                    color={color}
                    disabled={isDisabled}
                    onClick={onClick}
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: '8px',
                        backgroundColor: bgFor(theme, color),
                        color: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: hoverBgFor(theme, color),
                        },
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                            boxShadow: 'none',
                        },
                        ...((typeof sx === 'function' ? sx(theme) : sx) as object),
                    })}
                >
                    {loading ? <CircularProgress size={22} thickness={5} sx={{ color: 'inherit' }} /> : icon}
                </IconButton>
            </span>
        </Tooltip>
    );
};

export default BaseIconButtonDevice;
